<template>
  <div class="designerFormShell">
    <el-card class="card linkFormSection">
      <div slot="header">
        <strong class="linkFormSection__title">基本信息</strong>
      </div>
      <div class="linkFormBasic">
        <div class="linkFormBasic__media">
          <div class="linkFormMediaAnchor">
            <el-image
              v-if="avatarUrl"
              class="linkFormMediaAnchor__preview"
              :src="avatarUrl"
              fit="cover"
              :preview-src-list="[avatarUrl]"
            >
              <div slot="error" class="linkFormMediaAnchor__previewFallback">
                <i class="el-icon-picture-outline" aria-hidden="true" />
              </div>
            </el-image>
            <button
              v-else-if="!readonly"
              type="button"
              class="linkFormUploadZone linkFormUploadZone--anchor"
              :disabled="uploadLoading"
              @click="triggerAvatarUpload"
            >
              <i class="el-icon-upload2 linkFormUploadZone__icon" aria-hidden="true" />
              <span>上传头像</span>
              <span class="linkFormUploadZone__hint">jpg / png</span>
            </button>
            <div v-else class="linkFormMediaAnchor__noFile">
              <i class="el-icon-user linkFormMediaAnchor__noFileIcon" aria-hidden="true" />
              <span>还没有头像</span>
            </div>
          </div>
          <div v-if="avatarUrl && !readonly" class="linkFormMediaFileBar">
            <span class="linkFormMediaFileBar__name" :title="ruleForm.backgroundName">
              {{ ruleForm.backgroundName || '头像' }}
            </span>
            <div class="linkFormMediaFileBar__actions">
              <button
                type="button"
                class="linkFormMediaFileBar__btn"
                :disabled="uploadLoading"
                @click="triggerAvatarUpload"
              >
                替换
              </button>
              <button
                type="button"
                class="linkFormMediaFileBar__btn linkFormMediaFileBar__btn--danger"
                @click="clearAvatar"
              >
                移除
              </button>
            </div>
          </div>
          <el-upload
            ref="avatarUpload"
            class="avatarUploadHidden"
            action=""
            accept=".jpg,.png"
            :http-request="uploadAvatar"
            :show-file-list="false"
          />
          <el-form-item prop="backgroundUrl" class="avatarUrlField">
            <el-input v-show="false" v-model="ruleForm.backgroundUrl" />
          </el-form-item>
        </div>

        <div class="linkFormBasic__fields">
          <el-form-item label="设计师名称" prop="name">
            <el-input v-model="ruleForm.name" placeholder="请输入设计师名称" :disabled="readonly" />
          </el-form-item>
          <div v-if="!isCreate && ruleForm.key" class="keyLine">
            <span class="keyLine__label">Key</span>
            <code class="keyLine__value" :title="ruleForm.key">{{ ruleForm.key }}</code>
          </div>
          <div class="fieldPair">
            <el-form-item label="设计师类型" prop="type">
              <el-select
                v-model="ruleForm.type"
                placeholder="请选择设计师类型"
                :disabled="!isCreate || readonly"
              >
                <el-option
                  v-for="item in typeOptions"
                  :key="item.value"
                  :label="item.value"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="生日" prop="birthday">
              <el-date-picker
                v-if="!readonly"
                v-model="ruleForm.birthday"
                type="month"
                :placeholder="publicPlaceholder(ruleForm.birthdayPublic, '选择生日')"
                :disabled="ruleForm.birthdayPublic === 1"
                format="yyyy 年 MM 月"
                value-format="yyyy-MM"
                :picker-options="birthdayPickerOptions"
              />
              <el-input
                v-else
                :value="ruleForm.birthday"
                :placeholder="publicPlaceholder(ruleForm.birthdayPublic, '生日')"
                disabled
              />
            </el-form-item>
          </div>
          <el-form-item label="主页" prop="homeUrl">
            <el-input
              v-model="ruleForm.homeUrl"
              :placeholder="publicPlaceholder(ruleForm.homeUrlPublic, '个人主页地址，选填')"
              :disabled="readonly || ruleForm.homeUrlPublic === 1"
            />
          </el-form-item>
          <el-form-item label="简介" prop="introduction">
            <el-input
              v-model="ruleForm.introduction"
              type="textarea"
              :rows="3"
              placeholder="这位设计师是谁、擅长什么，选填"
              :disabled="readonly"
            />
          </el-form-item>
          <el-form-item label="标签" prop="tags">
            <div class="tagEditor">
              <el-tag
                v-for="tag in ruleForm.tags"
                :key="tag"
                :closable="!readonly"
                :disable-transitions="false"
                @close="removeTag(tag)"
              >
                {{ tag }}
              </el-tag>
              <template v-if="!readonly">
                <el-input
                  v-if="tagInputVisible"
                  ref="tagInput"
                  v-model="tagInputValue"
                  class="tagEditor__input"
                  size="small"
                  @keyup.enter.native="confirmTag"
                  @blur="confirmTag"
                />
                <el-button v-else size="small" @click="openTagInput">添加标签</el-button>
              </template>
              <span v-if="readonly && !(ruleForm.tags && ruleForm.tags.length)" class="cellEmpty">未填写</span>
            </div>
          </el-form-item>
        </div>
      </div>
    </el-card>

    <el-card class="card linkFormSection">
      <div slot="header">
        <strong class="linkFormSection__title">联系方式</strong>
      </div>
      <div class="fieldPair">
        <el-form-item label="Email">
          <el-input
            v-model="ruleForm.email"
            :placeholder="publicPlaceholder(ruleForm.emailPublic, '选填')"
            :disabled="readonly || ruleForm.emailPublic === 1"
          />
        </el-form-item>
        <el-form-item v-for="field in socialFields" :key="field.key" :label="field.label">
          <el-input
            v-model="ruleForm[field.key]"
            :placeholder="publicPlaceholder(ruleForm[field.publicKey], '选填，请输入地址')"
            :disabled="readonly || ruleForm[field.publicKey] === 1"
          />
        </el-form-item>
      </div>
    </el-card>

    <el-card v-if="!isCreate" class="card linkFormSection">
      <div slot="header">
        <strong class="linkFormSection__title">认证</strong>
      </div>
      <div class="fieldPair">
        <el-form-item label="认证状态">
          <el-switch
            v-model="ruleForm.isAuth"
            active-text="已认证"
            inactive-text="未认证"
            :active-value="1"
            :inactive-value="0"
            :disabled="readonly"
          />
        </el-form-item>
        <el-form-item label="认证时间">
          <el-input :value="ruleForm.authTime" disabled placeholder="认证时间" />
        </el-form-item>
        <el-form-item label="粉丝数">
          <el-input :value="ruleForm.followerCount" disabled />
        </el-form-item>
        <el-form-item label="关注数">
          <el-input :value="ruleForm.followingCount" disabled />
        </el-form-item>
      </div>
      <el-form-item label="认证信息">
        <el-input
          v-model="ruleForm.authMessage"
          type="textarea"
          :rows="3"
          placeholder="认证信息"
          :disabled="readonly"
        />
      </el-form-item>
    </el-card>
  </div>
</template>

<script>
import moment from 'moment'
import { DESIGNER_TYPE_OPTIONS } from './designerListHelpers'

const SOCIAL_FIELDS = [
  { key: 'facebook', publicKey: 'facebookIsPublic', label: 'Facebook' },
  { key: 'instagram', publicKey: 'instagramIsPublic', label: 'Instagram' },
  { key: 'whatsapp', publicKey: 'whatsappIsPublic', label: 'WhatsApp' },
  { key: 'telegram', publicKey: 'telegramIsPublic', label: 'Telegram' },
]

export default {
  name: 'DesignerFormShell',
  props: {
    ruleForm: { type: Object, required: true },
    isCreate: { type: Boolean, default: true },
    readonly: { type: Boolean, default: false },
    uploadLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      typeOptions: DESIGNER_TYPE_OPTIONS,
      socialFields: SOCIAL_FIELDS,
      tagInputVisible: false,
      tagInputValue: '',
      birthdayPickerOptions: {
        disabledDate(time) {
          return moment(time).isAfter(moment('2004-12-30'))
        },
      },
    }
  },
  computed: {
    avatarUrl() {
      return this.ruleForm.backgroundUrl || this.ruleForm.img || ''
    },
  },
  methods: {
    publicPlaceholder(isPublic, fallback) {
      return isPublic === 1 ? '仅用户可见' : fallback
    },
    triggerAvatarUpload() {
      const upload = this.$refs.avatarUpload
      if (upload && upload.$refs['upload-inner']) {
        upload.$refs['upload-inner'].$refs.input.click()
      }
    },
    uploadAvatar(request) {
      this.$emit('avatar-upload', request)
    },
    clearAvatar() {
      this.ruleForm.backgroundUrl = ''
      this.ruleForm.backgroundName = ''
      this.ruleForm.img = ''
    },
    removeTag(tag) {
      const index = this.ruleForm.tags.indexOf(tag)
      if (index >= 0) {
        this.ruleForm.tags.splice(index, 1)
      }
    },
    openTagInput() {
      this.tagInputVisible = true
      this.$nextTick(() => {
        const input = this.$refs.tagInput
        if (input && input.focus) {
          input.focus()
        }
      })
    },
    confirmTag() {
      const value = this.tagInputValue.trim()
      if (value && !this.ruleForm.tags.includes(value)) {
        this.ruleForm.tags.push(value)
      }
      this.tagInputVisible = false
      this.tagInputValue = ''
    },
  },
}
</script>

<style scoped>
.avatarUploadHidden {
  display: none;
}

.avatarUrlField {
  margin: 0;
}

.fieldPair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
}

.designerFormShell >>> .el-select,
.designerFormShell >>> .el-date-editor.el-input,
.designerFormShell >>> .el-textarea {
  width: 100%;
}

.keyLine {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -8px 0 12px;
  min-width: 0;
}

.keyLine__label {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--link-ink-muted, #909399);
}

.keyLine__value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
}

.tagEditor {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.tagEditor__input {
  width: 120px;
}

.cellEmpty {
  color: var(--link-ink-muted, #909399);
  font-size: 12px;
}

@media (max-width: 768px) {
  .fieldPair {
    grid-template-columns: 1fr;
  }
}
</style>
